import React, { useEffect, useMemo, useState } from 'react';
import { useConfirm, useToast } from '../context/UIContext';
import {
    addNotificationRecipient,
    deleteNotificationRecipient,
    getNotificationRecipients,
    updateNotificationRecipient,
} from '../services/NotificationRecipientService';
import { getAllUsers } from '../services/UserService';
import { extractApiError } from '../utils/apiError';
import '../styles/NotificationRecipients.css';

export const EVENTS = [
    { key: 'holiday', label: 'Urlaub' },
    { key: 'flex_day', label: 'Gleitzeit' },
    { key: 'overtime', label: 'Überstunden' },
];

const EMPTY_FORM = {
    email: '',
    name: '',
    events: { holiday: true, flex_day: true, overtime: true },
};

/** The stored subscriptions as one draft per event type. `all` is read from the row rather than
 *  from an empty `user_ids`, which cannot tell every employee from none. */
function draftFrom(recipient) {
    const byType = Object.fromEntries(
        (recipient.subscriptions || []).map(s => [s.event_type, s]),
    );
    return Object.fromEntries(EVENTS.map(({ key }) => {
        const sub = byType[key];
        return [key, {
            on: Boolean(sub),
            all: sub ? sub.all_users !== false : true,
            ids: ((sub && sub.user_ids) || []).map(String),
        }];
    }));
}

function subscriptionsFrom(draft) {
    return EVENTS
        .filter(({ key }) => draft[key].on)
        .map(({ key }) => ({
            event_type: key,
            all_users: draft[key].all,
            user_ids: draft[key].all ? [] : draft[key].ids,
        }));
}

/** Who is told about this event type, as the table prints it. */
function summarise(recipient, eventKey, nameById) {
    const sub = (recipient.subscriptions || []).find(s => s.event_type === eventKey);
    if (!sub) return { text: '—', muted: true };
    if (sub.all_users !== false) return { text: 'alle Mitarbeiter', muted: false };
    const names = (sub.user_ids || []).map(id => nameById[String(id)] || id);
    return { text: names.join(', ') || 'niemand', muted: names.length === 0 };
}

/** Mailbenachrichtigung bei Urlaub/Gleitzeit/Überstunden: the addresses the organisation notifies,
 *  each one saying per event type whether it wants every employee or only named ones. */
export default function NotificationRecipients() {
    const toast = useToast();
    const confirm = useConfirm();

    const [recipients, setRecipients] = useState([]);
    const [people, setPeople] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [editing, setEditing] = useState(null);
    const [draft, setDraft] = useState(null);

    const load = async () => {
        try {
            const [list, users] = await Promise.all([getNotificationRecipients(), getAllUsers()]);
            setRecipients(list);
            setPeople((users || []).filter(u => u.is_active !== false));
        } catch (e) {
            toast(extractApiError(e, 'Empfänger konnten nicht geladen werden.'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const nameById = useMemo(
        () => Object.fromEntries(people.map(p => [String(p.id), p.name || p.username])),
        [people],
    );

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await addNotificationRecipient({
                email: form.email.trim(),
                name: form.name.trim() || null,
                subscriptions: EVENTS
                    .filter(({ key }) => form.events[key])
                    .map(({ key }) => ({ event_type: key, all_users: true, user_ids: [] })),
            });
            setForm(EMPTY_FORM);
            toast('Empfänger hinzugefügt.');
            await load();
        } catch (err) {
            toast(extractApiError(err, 'Empfänger konnte nicht hinzugefügt werden.'));
        } finally {
            setSaving(false);
        }
    };

    const remove = async (recipient) => {
        const ok = await confirm(
            `Empfänger ${recipient.email} entfernen? Die Adresse erhält danach keine Benachrichtigungen mehr.`,
            { confirmLabel: 'Entfernen', danger: true },
        );
        if (!ok) return;
        try {
            await deleteNotificationRecipient(recipient.id);
            toast('Empfänger entfernt.');
            await load();
        } catch (e) {
            toast(extractApiError(e, 'Empfänger konnte nicht entfernt werden.'));
        }
    };

    const openEditor = (recipient) => {
        setEditing(recipient.id);
        setDraft(draftFrom(recipient));
    };

    const patchEvent = (key, change) => {
        setDraft(cur => ({ ...cur, [key]: { ...cur[key], ...change } }));
    };

    const togglePerson = (key, userId) => {
        const id = String(userId);
        setDraft((cur) => {
            const ids = cur[key].ids.includes(id)
                ? cur[key].ids.filter(x => x !== id)
                : [...cur[key].ids, id];
            return { ...cur, [key]: { ...cur[key], ids } };
        });
    };

    const save = async (recipientId) => {
        // The API refuses a named subscription with nobody in it; saying so here names the type.
        const empty = EVENTS.find(({ key }) => draft[key].on && !draft[key].all
            && draft[key].ids.length === 0);
        if (empty) {
            toast(`Bitte mindestens einen Mitarbeiter für „${empty.label}" auswählen.`);
            return;
        }
        setSaving(true);
        try {
            // Always sent, even empty: that is how an address is unsubscribed again.
            await updateNotificationRecipient(recipientId, {
                subscriptions: subscriptionsFrom(draft),
            });
            setEditing(null);
            toast('Benachrichtigungen gespeichert.');
            await load();
        } catch (e) {
            toast(extractApiError(e, 'Benachrichtigungen konnten nicht gespeichert werden.'));
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <p className="nr-empty">Wird geladen…</p>;

    return (
        <div className="nr-wrap">
            <p className="nr-intro">
                Diese Adressen werden benachrichtigt, sobald ein Antrag zur Genehmigung offen
                ist. Jede Adresse entscheidet selbst, über welche Art von Antrag und über welche
                Mitarbeiter sie informiert wird.
            </p>

            <form className="nr-form" onSubmit={submit}>
                <label className="nr-field nr-field--grow">
                    E-Mail-Adresse
                    <input
                        type="email"
                        className="input"
                        required
                        placeholder="info@firma.de"
                        value={form.email}
                        onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                    />
                </label>
                <label className="nr-field">
                    Bezeichnung
                    <input
                        type="text"
                        className="input"
                        placeholder="z. B. Büro"
                        value={form.name}
                        onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                    />
                </label>
                <div className="nr-field">
                    Benachrichtigen über
                    <div className="nr-kinds">
                        {EVENTS.map(({ key, label }) => (
                            <label key={key} className="nr-kind">
                                <input
                                    type="checkbox"
                                    checked={form.events[key]}
                                    onChange={() => setForm(p => ({
                                        ...p,
                                        events: { ...p.events, [key]: !p.events[key] },
                                    }))}
                                />
                                <span>{label}</span>
                            </label>
                        ))}
                    </div>
                </div>
                <button type="submit" className="button" disabled={saving || !form.email.trim()}>
                    Hinzufügen
                </button>
            </form>

            {recipients.length === 0 ? (
                <p className="nr-empty">
                    Noch keine Empfänger eingetragen — es werden keine Benachrichtigungen versendet.
                </p>
            ) : (
                <table className="table nr-table table--cards">
                    <thead>
                        <tr>
                            <th>E-Mail</th>
                            {EVENTS.map(({ key, label }) => <th key={key}>{label}</th>)}
                            <th aria-label="Aktionen" />
                        </tr>
                    </thead>
                    <tbody>
                        {recipients.map((recipient) => {
                            const isEditing = editing === recipient.id;
                            return (
                                <React.Fragment key={recipient.id}>
                                    <tr>
                                        <td data-label="E-Mail">
                                            <span className="nr-email">{recipient.email}</span>
                                            {recipient.name && (
                                                <span className="nr-name">{recipient.name}</span>
                                            )}
                                        </td>
                                        {EVENTS.map(({ key, label }) => {
                                            const cell = summarise(recipient, key, nameById);
                                            return (
                                                <td
                                                    key={key}
                                                    data-label={label}
                                                    className={cell.muted ? 'nr-muted' : undefined}
                                                >
                                                    {cell.text}
                                                </td>
                                            );
                                        })}
                                        <td className="table-card-actions">
                                            <button
                                                type="button"
                                                className="button btn-sm"
                                                onClick={() => (isEditing
                                                    ? setEditing(null)
                                                    : openEditor(recipient))}
                                            >
                                                Bearbeiten
                                            </button>
                                            <button
                                                type="button"
                                                className="button button-danger btn-sm"
                                                onClick={() => remove(recipient)}
                                            >
                                                Entfernen
                                            </button>
                                        </td>
                                    </tr>
                                    {isEditing && draft && (
                                        <tr className="nr-editor-row">
                                            <td colSpan={EVENTS.length + 2}>
                                                <div className="nr-editor">
                                                    {EVENTS.map(({ key, label }) => (
                                                        <fieldset key={key} className="nr-event">
                                                            <legend>
                                                                <label className="nr-kind">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={draft[key].on}
                                                                        onChange={() => patchEvent(key, { on: !draft[key].on })}
                                                                    />
                                                                    <span>{label}</span>
                                                                </label>
                                                            </legend>
                                                            {draft[key].on && (
                                                                <>
                                                                    <div className="nr-scope">
                                                                        <label className="nr-kind">
                                                                            <input
                                                                                type="radio"
                                                                                name={`${recipient.id}-${key}-scope`}
                                                                                checked={draft[key].all}
                                                                                onChange={() => patchEvent(key, { all: true })}
                                                                            />
                                                                            <span>alle Mitarbeiter</span>
                                                                        </label>
                                                                        <label className="nr-kind">
                                                                            <input
                                                                                type="radio"
                                                                                name={`${recipient.id}-${key}-scope`}
                                                                                checked={!draft[key].all}
                                                                                onChange={() => patchEvent(key, { all: false })}
                                                                            />
                                                                            <span>nur ausgewählte</span>
                                                                        </label>
                                                                    </div>
                                                                    {!draft[key].all && (
                                                                        <div className="nr-people">
                                                                            {people.map(person => (
                                                                                <label
                                                                                    key={person.id}
                                                                                    className="nr-kind"
                                                                                >
                                                                                    <input
                                                                                        type="checkbox"
                                                                                        checked={draft[key].ids.includes(String(person.id))}
                                                                                        onChange={() => togglePerson(key, person.id)}
                                                                                    />
                                                                                    <span>{person.name || person.username}</span>
                                                                                </label>
                                                                            ))}
                                                                        </div>
                                                                    )}
                                                                </>
                                                            )}
                                                        </fieldset>
                                                    ))}
                                                    <div className="nr-editor-actions">
                                                        <button
                                                            type="button"
                                                            className="button btn-sm"
                                                            disabled={saving}
                                                            onClick={() => save(recipient.id)}
                                                        >
                                                            Speichern
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="button btn-sm"
                                                            onClick={() => setEditing(null)}
                                                        >
                                                            Abbrechen
                                                        </button>
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
            )}
        </div>
    );
}
